import {merge, Observable, of as observableOf, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, tap} from 'rxjs/operators';
import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {NgbModule, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {AbstractSigaleComponent} from '../abstract-sigale.component';

@Component({
	standalone: true,
	selector: 'app-autocomplete',
	templateUrl: './autocomplete.component.html',
	styleUrls: ['./autocomplete.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: AutocompleteComponent,
			multi: true
		}
	],
	imports: [
		CommonModule,
		FormsModule,
		NgbModule
	]
})
export class AutocompleteComponent extends AbstractSigaleComponent implements ControlValueAccessor {

	@Input('ws') ws: string; // url du service
	@Input('suggestion') suggestion: any[];
	@Input('limit') limit = 10; // limite de suggestion
	@Input('delay') delay = 500; // delay entre chaque requettes
	@Input('nombreCaractereMin') nombreCaractereMin = 1; // le nombre min de caractère avant de lancer la recherche (si zero recherche on focus)
	@Input('disabled') disabled = false;
	@Input('strict') strict = false; // si on peut selectionner une valeur non dans la liste
	@Input('placeholder') placeholder = '';
	@Input('inputId') inputId = null;
	@Input('classInput') classInput = '';
	@Input('useCase') useCase: string;
	@Input('appendTo') appendTo = null;
	@Input() formControl: FormControl = new FormControl();
	@Input('loaderInput') loaderInput: Boolean = false;
	@Input('maxlength') maxlength: number;

	@Input('formatter') formatter: any; // formatteur global
	@Input('inputFormatter') inputFormatter: any; // formatteur sur le champ d'input prioritaire sur le global
	@Input('resultFormatter') resultFormatter: any; // formatteur sur les resultats

	@Output('onSuccess') onSuccess: EventEmitter<string> = new EventEmitter<string>(); // quand la recherche de la liste d'objet est fini
	@Output('onError') onError: EventEmitter<string> = new EventEmitter<string>(); // si la recherche de la liste d'objet a une erreur

	@Output('focus') focus: EventEmitter<string> = new EventEmitter<string>(); // si la recherche de la liste d'objet a une erreur

	focus$ = new Subject<string>();
	@ViewChild(NgbTypeahead, {static: true}) private typeahead: any;

	model: any;
	searching = false;
	placement: string;

	onChange;
	onTouched;

	constructor(private http: HttpClient) {
		super();
	}

	writeValue(value: any): void {
		this.model = value;
		this.typeahead.___value = value;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	onBlur() {
		this.searching = false;
	}

	change(item) {
		this.onChange(item);
		this.onTouched(item);
		this.model = item;
	}

	public clearItem() {
		this.change(null);
	}

	searchInSuggestion(filtre: string) {
		return this.suggestion.filter(valeur => {
				if (valeur.designation) {
					return valeur.designation.toUpperCase().includes(filtre.toUpperCase());
				}
				if (valeur.code) {
					return valeur.code.toUpperCase().startsWith(filtre.toUpperCase());
				}
			}
		).splice(0, this.limit);
	}

	search = (filtre: Observable<string>): Observable<readonly any[]> => {
		// @ts-ignore
		return merge(filtre.pipe(debounceTime(this.delay), distinctUntilChanged()), // evite le spam de requêtes lors de la saisie
				this.focus$).pipe(
				tap(() => {
					this.searching = true;
					// recherche du meilleur placement pour afficher les suggestions
					this.placement = this.typeahead._nativeElement.getBoundingClientRect().top < window.innerHeight / 2 ? 'bottom-left' : 'top-left';
				}),
				switchMap(
						filtre => {
							if (filtre.length < this.nombreCaractereMin) {
								return observableOf([]);
							}
							let params: any;
							let serviceCall: any;

							params = new HttpParams().set('filter', filtre.trim())
									.set('limit', this.limit.toString());

							if (this.useCase) {
								params = params.append('use-case', this.useCase);
							}
							if (this.ws) {
								serviceCall = this.http.get(this.ws, {params: params});
							}
							else if (this.suggestion) {
								serviceCall = observableOf(this.searchInSuggestion(filtre));
							}
							else {
								serviceCall = observableOf(null);
							}
							return serviceCall.pipe(
									tap(() => this.onSuccess.emit(null)),
									catchError(() => {
										this.onError.emit(null);
										return observableOf(null);
									})
							);
						}),
				tap(() => this.searching = false));
	}

	ngOnInit() {
		super.ngOnInit();

		this.typeahead.dismissPopup = function () {
			if (this.isPopupOpen()) {
				this._closePopup();
			}
			if (!this.editable && this._nativeElement.value != (this.___value ? this.inputFormatter(this.___value) : '')) {
				this._nativeElement.value = '';
			}
		};

		this.typeahead._selectResult = function (result) {
			let defaultPrevented = false;
			this.___value = result;
			this.selectItem.emit({
				item: result, preventDefault: function () {
					defaultPrevented = true;
				}
			});
			if (!defaultPrevented) {
				this.writeValue(result);
				this._onChange(result);
			}
		};
		let Key;
		(function (Key) {
			Key[Key['Tab'] = 9] = 'Tab';
			Key[Key['Enter'] = 13] = 'Enter';
			Key[Key['Escape'] = 27] = 'Escape';
			Key[Key['ArrowUp'] = 38] = 'ArrowUp';
			Key[Key['ArrowDown'] = 40] = 'ArrowDown';
		})(Key || (Key = {}));
		this.typeahead.handleKeyDown = function (event) {
			if (event.which == Key.Enter && this.editable) {
				if (this.isPopupOpen()) {
					event.preventDefault();
					const result = this._windowRef.instance.getActive();
					if (result !== undefined && result !== null) {
						this._selectResult(result);
						this._closePopup();
						return;
					}
					event.stopPropagation();
				}
				this._selectResult(this._nativeElement.value);
				this._nativeElement.value = this.___value;
				this._closePopup();
			}
			if (!this.isPopupOpen()) {
				return;
			}
			if (Key[(event.which !== undefined && event.which !== null) ? '' + event.which : '']) {
				switch (event.which) {
					case Key.ArrowDown:
						event.preventDefault();
						this._windowRef.instance.next();
						this._showHint();
						break;
					case Key.ArrowUp:
						event.preventDefault();
						this._windowRef.instance.prev();
						this._showHint();
						break;
					case Key.Enter:
					case Key.Tab:
						const result = this._windowRef.instance.getActive();
						if (result !== undefined && result !== null) {
							event.preventDefault();
							event.stopPropagation();
							this._selectResult(result);
						}
						this._closePopup();
						break;
					case
					Key.Escape:
						event.preventDefault();
						event.stopPropagation();
						this.dismissPopup();
						break;
				}
			}
		};

		if (this.model) {
			this.typeahead._selectResult(this.model);
		}
	}

	openOnFocus(eventTarget: EventTarget) {
		const value = (<HTMLInputElement> eventTarget).value;
		if (this.nombreCaractereMin === 0 && (!value || !value.length)) {
			this.focus$.next(value);
		}
		this.focus.emit(value);
	}
}
